import { useInfiniteQuery } from 'react-query'
import { UsersFiltersDto } from '@merchx-v2/shared-types'
import hash from 'object-hash'
import axios from 'utils/axios'
import { guardFromErrors, extractErrorInfo } from 'utils/graphqlHelpers'
import { GraphQLUsersListPageItem } from '../types'

const DEFAULT_PAGE_SIZE = 15

export type UsersPage = {
  items: GraphQLUsersListPageItem[]
  total: number
  currentPage: number
  pageSize: number
  filters?: UsersFiltersDto
}

/// ////////////////////////////////////////////////////////////////////////////////
// GRAPHQL
/// ////////////////////////////////////////////////////////////////////////////////

type QueryResponse = GraphQLResponse<'usersListPage', UsersPage>

const FETCH_USER_LIST = `
query usersListPage ($page: Int, $size: Int, $filters: UsersListPageFilters!) {
  usersListPage (page: $page, size: $size, filters: $filters) {
    items {
      id
      email
      firstName
      lastName
      lastLoginAt
      bannedTill
      isEmailConfirmed
      createdAt

    }
    total
  }
}
`

/// ////////////////////////////////////////////////////////////////////////////////
// FUNCTION
/// ////////////////////////////////////////////////////////////////////////////////

type PageParam = {
  page?: number
  size?: number
  filters: UsersFiltersDto
}

type FetchUsersListArgs = {
  queryKey: string | string[]
  pageParam?: PageParam
}

type FetchUsersList = (args: FetchUsersListArgs) => Promise<UsersPage>

const fetchUsersList: FetchUsersList = async ({ pageParam }) => {
  const { page = 1, size = DEFAULT_PAGE_SIZE, filters = undefined } = pageParam || {}

  try {
    const {
      data: { data, errors }
    }: QueryResponse = await axios.post('/graphql', {
      query: FETCH_USER_LIST,
      variables: { page, size, filters }
    })

    guardFromErrors(errors)

    if (!data) {
      throw new Error('Response body is empty!')
    }

    const { usersListPage } = data
    if (!usersListPage) {
      throw new Error("Can't get users list page!")
    }

    return {
      items: usersListPage.items,
      total: usersListPage.total,
      currentPage: page,
      pageSize: size,
      filters
    }
  } catch (error) {
    throw new Error(extractErrorInfo(error))
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK
/// ////////////////////////////////////////////////////////////////////////////////

type GetNextPageParam = (args: UsersPage) => PageParam | undefined

type UsersListEvents = {
  keepPreviousData: boolean
  getPreviousPageParam: GetNextPageParam
  getNextPageParam: GetNextPageParam
}

const usersListEvents: UsersListEvents = {
  keepPreviousData: true,
  getPreviousPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0, filters }) => {
    if (currentPage > 1) {
      return {
        page: currentPage - 1,
        size: pageSize,
        filters
      }
    }

    return undefined
  },
  getNextPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0, total = 0, filters }) => {
    if (pageSize * currentPage < total) {
      return {
        page: currentPage + 1,
        size: pageSize,
        filters
      }
    }
    return undefined
  }
}

export const useUsersList = (filters: UsersFiltersDto) => {
  const cacheKey = ['users', hash(filters)]

  return useInfiniteQuery(
    cacheKey,
    (prev) =>
      fetchUsersList({
        queryKey: cacheKey,
        pageParam: { ...prev.pageParam, filters }
      }),
    usersListEvents
  )
}
